import React, { ReactElement, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    alpha,
} from '@mui/material';
import { Close, CheckCircle, Warning, Download } from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';

export interface ImportValidationResult {
    status: 'validation_complete';
    header_mapping?: Array<{
        original: string;
        mapped_to: string;
        was_mapped: boolean;
    }>;
    duplicates: {
        csv_internal: Array<{
            name: string;
            first_name: string;
            last_name: string;
            row_numbers: number[];
            count: number;
        }>;
        existing_members: Array<{
            name: string;
            first_name: string;
            last_name: string;
            row_number: number;
        }>;
    };
    malformed?: Array<{
        row: number;
        name: string;
        first_name: string;
        last_name: string;
        issues: string[];
        suggestion: string;
    }>;
    summary: {
        total_rows: number;
        unique_rows: number;
        duplicate_rows: number;
        existing_matches: number;
        malformed_rows?: number;
    };
    import_token: string;
}

interface MemberImportErrorReviewProps {
    open: boolean;
    validationResult: ImportValidationResult;
    onConfirm: (action: 'skip_duplicates' | 'cancel') => void;
    onClose: () => void;
}

export default function MemberImportErrorReview({
    open,
    validationResult,
    onConfirm,
    onClose,
}: MemberImportErrorReviewProps): ReactElement {
    const theme = useTheme();
    const [selectedTab, setSelectedTab] = useState(0);

    const { summary, duplicates, malformed } = validationResult;

    // Glassmorphism card style
    const glassCardStyle = {
        background: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 3,
        boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.1)}`,
    };

    const handleDownloadReport = () => {
        const csvContent = generateCSVReport();
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'import_error_report.csv';
        link.click();
    };

    const generateCSVReport = (): string => {
        let csv = 'Type,Name,Details,Row(s)\n';

        duplicates.csv_internal.forEach((dup) => {
            csv += `"CSV Duplicate","${dup.name}","Appears ${
                dup.count
            } times","${dup.row_numbers.join(', ')}"\n`;
        });

        duplicates.existing_members.forEach((dup) => {
            csv += `"Already Exists","${dup.name}","Already in group","${dup.row_number}"\n`;
        });

        malformed?.forEach((mal) => {
            csv += `"Error","${mal.name}","${mal.issues.join('; ')}","${mal.row}"\n`;
        });

        return csv;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    ...glassCardStyle,
                    minHeight: '600px',
                },
            }}
        >
            <DialogTitle sx={{ padding: theme.spacing(4, 6, 2) }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={2}>
                        <Warning color="warning" fontSize="large" />
                        <Typography variant="h5" fontWeight={600}>
                            Import Review Required
                        </Typography>
                    </Box>
                    <IconButton aria-label="close" onClick={onClose} size="large">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ padding: theme.spacing(0, 6, 4) }}>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={glassCardStyle}>
                            <CardContent>
                                <Typography variant="h4" fontWeight={700} color="primary">
                                    {summary.total_rows}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Rows Uploaded
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={glassCardStyle}>
                            <CardContent>
                                <Typography variant="h4" fontWeight={700} color="success.main">
                                    {summary.unique_rows}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Ready to Import
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={glassCardStyle}>
                            <CardContent>
                                <Typography variant="h4" fontWeight={700} color="warning.main">
                                    {summary.duplicate_rows}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    CSV Duplicates
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={glassCardStyle}>
                            <CardContent>
                                <Typography variant="h4" fontWeight={700} color="info.main">
                                    {summary.existing_matches}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Already in Group
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Info Box */}
                <Box
                    sx={{
                        ...glassCardStyle,
                        p: 3,
                        mb: 3,
                        borderLeft: `4px solid ${theme.palette.info.main}`,
                    }}
                >
                    <Typography variant="body2" color="textSecondary">
                        We found some issues with your import. Review the details below. Click{' '}
                        <strong>&quot;Import {summary.unique_rows} Unique Members&quot;</strong> to
                        proceed (duplicates will be automatically skipped) or{' '}
                        <strong>&quot;Cancel&quot;</strong> to fix the CSV and re-upload.
                    </Typography>
                </Box>

                {/* Tabs */}
                <Box sx={glassCardStyle}>
                    <Tabs
                        value={selectedTab}
                        onChange={(_, newValue) => setSelectedTab(newValue)}
                        sx={{
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            px: 2,
                        }}
                    >
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2">Duplicates in CSV</Typography>
                                    <Chip
                                        label={duplicates.csv_internal.length}
                                        size="small"
                                        color="warning"
                                    />
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2">Already Exist</Typography>
                                    <Chip
                                        label={duplicates.existing_members.length}
                                        size="small"
                                        color="info"
                                    />
                                </Box>
                            }
                        />
                        {malformed && malformed.length > 0 && (
                            <Tab
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2">Errors</Typography>
                                        <Chip label={malformed.length} size="small" color="error" />
                                    </Box>
                                }
                            />
                        )}
                    </Tabs>

                    <Box sx={{ p: 3, maxHeight: '300px', overflowY: 'auto' }}>
                        {/* Tab 1: CSV Duplicates */}
                        {selectedTab === 0 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Name
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    First Seen (Row)
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Duplicates (Rows)
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Action
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {duplicates.csv_internal.map((dup, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {dup.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={dup.row_numbers[0]}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {dup.row_numbers.slice(1).map((row) => (
                                                        <Chip
                                                            key={row}
                                                            label={row}
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                            sx={{ mr: 0.5 }}
                                                        />
                                                    ))}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Keep first, skip others
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {duplicates.csv_internal.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        No duplicates found in CSV
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Tab 2: Already Exist */}
                        {selectedTab === 1 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Name
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    CSV Row
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Action
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {duplicates.existing_members.map((dup, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {dup.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={dup.row_number}
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Skip (already in group)
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {duplicates.existing_members.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        No existing members found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Tab 3: Errors */}
                        {selectedTab === 2 && malformed && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Row
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Name
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Issue
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Suggestion
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {malformed.map((error, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>
                                                    <Chip
                                                        label={error.row}
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {error.name || '(empty)'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {error.issues.map((issue, i) => (
                                                        <Chip
                                                            key={i}
                                                            label={issue}
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                            sx={{ mr: 0.5, mb: 0.5 }}
                                                        />
                                                    ))}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {error.suggestion}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Box>

                {/* Action Buttons */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 4 }}
                >
                    <Button
                        variant="text"
                        startIcon={<Download />}
                        onClick={handleDownloadReport}
                        sx={{
                            color: theme.palette.text.secondary,
                        }}
                    >
                        Download Error Report
                    </Button>

                    <Box display="flex" gap={2}>
                        <Button
                            variant="outlined"
                            onClick={() => onConfirm('cancel')}
                            sx={{
                                borderColor: alpha(theme.palette.divider, 0.3),
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    borderColor: alpha(theme.palette.divider, 0.5),
                                    background: alpha(theme.palette.background.paper, 0.5),
                                },
                            }}
                        >
                            Cancel Import
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<CheckCircle />}
                            onClick={() => onConfirm('skip_duplicates')}
                            sx={{
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                                '&:hover': {
                                    boxShadow: `0 6px 24px ${alpha(
                                        theme.palette.primary.main,
                                        0.5,
                                    )}`,
                                },
                            }}
                        >
                            Import {summary.unique_rows} Unique Members
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
